import { customerApiClient } from './customer-api-client';
import type {
  CustomerLoginResponse,
  CustomerMeProfile,
  CustomerPortalUser,
  UpdateMyContactPayload,
} from '@/types/customer-auth.types';
import { recoverySessionHeaders, writeRecoverySessionId } from '@/lib/recovery-session';

/**
 * B6.14 UI closure — the three existing password endpoints, which had no frontend
 * caller until now. They are the same three stages a customer uses to recover a
 * forgotten password and to accept an invitation: the backend deliberately makes no
 * distinction, because `resetPassword()` has always accepted a contact that has no
 * password yet. No new endpoint was added for activation.
 */

/** Stage 1 response. Enumeration-safe: identical whether or not the email exists. */
export interface CustomerOtpRequestResult {
  message: string;
  maskedEmail: string;
  /** Constant for every caller — the client derives its own countdown from it. */
  cooldownSeconds: number;
  otpExpiresAt: string;
  /**
   * D-143 — browser-scoped session counter. `sessionRequestsUsed` counts requests
   * made from THIS browser, not the account's OTP usage: the account figure would
   * differ between a real and an unknown address and so may never be returned to
   * an anonymous caller. `sessionRequestLimit` is the policy value 5.
   */
  recoverySessionId: string;
  sessionRequestsUsed: number;
  sessionRequestLimit: number;
}

/** Stage 2 response. The reset token is short-lived and held in memory only. */
export interface CustomerOtpVerifyResult {
  resetToken: string;
  expiresAt: string;
}

export const customerAuthService = {
  login: (identifier: string, password: string): Promise<CustomerLoginResponse> =>
    customerApiClient
      .post<CustomerLoginResponse>('/customer-portal/auth/login', { identifier, password })
      .then((r) => r.data),

  getMe: (accessToken?: string): Promise<CustomerPortalUser> =>
    customerApiClient
      .get<CustomerPortalUser>('/customer-portal/auth/me', {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      })
      .then((r) => r.data),

  logout: (refreshToken: string): Promise<void> =>
    customerApiClient.post('/customer-portal/auth/logout', { refreshToken }).then(() => undefined),

  // ── User Profile P2 — self-service account. No id anywhere: the subject is the token. ──

  /** The full account projection (superset of `getMe`). */
  getMeProfile: (): Promise<CustomerMeProfile> =>
    customerApiClient.get<CustomerMeProfile>('/customer-portal/auth/me').then((r) => r.data),

  /** Writes only the four self-editable fields; the server refuses anything else with 400. */
  updateMe: (payload: UpdateMyContactPayload): Promise<CustomerMeProfile> =>
    customerApiClient
      .patch<CustomerMeProfile>('/customer-portal/auth/me', payload)
      .then((r) => r.data),

  /**
   * Changes the signed-in contact's password. Returns 204; the backend bumps
   * `tokenVersion` AND revokes every refresh family — including this session's —
   * so the caller must sign out deliberately afterwards (Owner decision P2-OD-3).
   */
  changePassword: (currentPassword: string, newPassword: string): Promise<void> =>
    customerApiClient
      .patch('/customer-portal/auth/password/change', { currentPassword, newPassword })
      .then(() => undefined),

  /** Stage 1 — send a 6-digit OTP to the contact's email. */
  requestPasswordOtp: (email: string): Promise<CustomerOtpRequestResult> =>
    customerApiClient
      .post<CustomerOtpRequestResult>(
        '/customer-portal/auth/password/request',
        { email },
        // D-143 — mirrors the staff service exactly; the two planes must not
        // diverge in what they send or in what the counter means.
        { headers: recoverySessionHeaders() },
      )
      .then((r) => {
        writeRecoverySessionId(r.data?.recoverySessionId);
        return r.data;
      }),

  /** Stage 2 — exchange the OTP for a short-lived reset token. */
  verifyPasswordOtp: (email: string, otp: string): Promise<CustomerOtpVerifyResult> =>
    customerApiClient
      .post<CustomerOtpVerifyResult>('/customer-portal/auth/password/verify-otp', { email, otp })
      .then((r) => r.data),

  /**
   * Stage 3 — set the password. Returns 204; the backend increments `tokenVersion`,
   * so any session anywhere is invalidated and the customer signs in fresh.
   */
  resetPassword: (
    resetToken: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<void> =>
    customerApiClient
      .post('/customer-portal/auth/password/reset', { resetToken, newPassword, confirmPassword })
      .then(() => undefined),
};
